import { startTransition, useEffect, useMemo, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { FilterValue, IndexablePage } from "../../../components/types/types";
import { HomeRouteMode } from "./useHomeRouteMode";
import { TypeResto } from "../../../components/types/restaurantTypes";

interface Params {
  mode: HomeRouteMode;
  routeCity?: string;
  seoPage: IndexablePage | null;
  indexablePagesLoaded: boolean;
  indexablePages: IndexablePage[];
  filterChoose: FilterValue[];
  setFilterChoose: Dispatch<SetStateAction<FilterValue[]>>;
  setFinalCityLoad: Dispatch<SetStateAction<string>>;
  setFinalCityLoadBrut: Dispatch<SetStateAction<string>>;
  setValueSearchCity: (value: string) => void;
  searchQuery: string;
}

const normalizePath = (path: string) => {
  const clean = path.trim();
  if (!clean || clean === "/") return "/";
  return clean.startsWith("/") ? clean : `/${clean}`;
};

const hasFilter = (filters: FilterValue[], name?: string | null) =>
  !!name && filters.some((f) => typeof f === "string" && f === name);
const SPECIAL_FILTER_PREFIXES = [
  "dish:",
  "regime:",
  "country:",
  "cultural-region:",
  "french-region:",
  "cuisine:",
  "cuisine-cultural-region:",
  "cuisine-french-region:",
  "concept-unique:",
] as const;

export const isSpecialRestaurantType = (value?: string | null) => {
  if (!value) return false;
  return SPECIAL_FILTER_PREFIXES.some(
    (prefix) => value.startsWith(prefix)
  );
};
const getFilterValue = (page: IndexablePage | null) => {
  if (!page) return null;
  if(isSpecialRestaurantType(page.filter_slug)) return page.filter_slug
  else return page.filter_name || null;
}
const getFilterValues = (page: IndexablePage | null) => {
  if (!page) return [];
  const values: string[] = [];
  if (page.filter_name) values.push(page.filter_name);
  if (isSpecialRestaurantType(page.filter_slug)) values.push(page.filter_slug || '');

  return values;
};


export function useIndexableRouteSync({
  mode,
  routeCity,
  seoPage,
  indexablePagesLoaded,
  indexablePages,
  filterChoose,
  setFilterChoose,
  setFinalCityLoad,
  setFinalCityLoadBrut,
  setValueSearchCity,
  searchQuery
}: Params) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const skipMissingUrlFilterNameRef = useRef<string | null>(null);
  const preventPromotionForSignatureRef = useRef<string | null>(null);

  const removeFilterFromSearch = (search: string,filterToRemove: string) => {
    const params = new URLSearchParams(search);
    const filters = params.getAll("f").filter((filter) => filter !== filterToRemove);
    params.delete("f");
    filters.forEach((filter) => {
      params.append("f", filter);
    });
    const value = params.toString();
    return value ? `?${value}` : "";
  };

  const currentPath = normalizePath(location.pathname);
   const filterSignature = useMemo(() => {
    try {
      return JSON.stringify(filterChoose);
    } catch {
      return String(filterChoose);
    }
  }, [filterChoose]);

    const isCleaningAfterPopRef = useRef(false);
    useEffect(() => {
      if (currentPath !== "/") return;
      if (navigationType !== "POP") return;
      if (location.search) return;

      isCleaningAfterPopRef.current = true;

      setFinalCityLoad("");
      setFinalCityLoadBrut("");
      setValueSearchCity("");

      startTransition(() => {
        setFilterChoose([]);
      });
    }, [currentPath, navigationType,location.search,setFilterChoose,setFinalCityLoad,setFinalCityLoadBrut,setValueSearchCity,]);
    useEffect(() => {
      if (!isCleaningAfterPopRef.current) return;
      if (currentPath === "/" && filterChoose.length === 0) {
        isCleaningAfterPopRef.current = false;
      }
    }, [currentPath, filterChoose]);

  const currentUrlPage = useMemo(() => {
    const matchedPage = indexablePages.find((page) => page.slug_path && normalizePath(page.slug_path) === currentPath);
    if (matchedPage) return matchedPage;
    if (seoPage?.slug_path &&normalizePath(seoPage.slug_path) === currentPath) return seoPage;
    return null;
  }, [indexablePages, currentPath, seoPage]);
  const isMajorFilter = (value: string) => {
    if (TypeResto.includes(value)) return true;
    if (isSpecialRestaurantType(value)) {
      return true;
    }
    const matchingPage = indexablePages.find((page) => getFilterValues(page).includes(value) );
    return (
      matchingPage?.filter_type === "type_resto" ||
      matchingPage?.filter_type === "cuisine" ||
      matchingPage?.filter_type === "regime"
    );

  };
  
  const firstUrlFilterName = useMemo(() => {
    const value = getFilterValue(seoPage);

    if (!value) return null;
    if (mode !== "filter" && mode !== "city_filter") return null;

    return value;
  }, [seoPage, mode]);

  useEffect(() => {
    if (!firstUrlFilterName) return;

    skipMissingUrlFilterNameRef.current = firstUrlFilterName;
    startTransition(() => {
      setFilterChoose((prev) => {
        if (hasFilter(prev, firstUrlFilterName)) return prev;
        return [...prev, firstUrlFilterName];
      });
    });
  }, [firstUrlFilterName, setFilterChoose]);

  useEffect(() => {
    if (!firstUrlFilterName) return;

    if ( skipMissingUrlFilterNameRef.current === firstUrlFilterName && hasFilter(filterChoose, firstUrlFilterName)) {
      skipMissingUrlFilterNameRef.current = null;
    }
  }, [firstUrlFilterName, filterChoose]);

  useEffect(() => {
    if (!indexablePagesLoaded) return;

    const seoFilterValue = getFilterValue(currentUrlPage);
    if (!seoFilterValue) return;
    const stringFilters = filterChoose.filter((filter): filter is string => typeof filter === "string");
    const activeMajorFilters = stringFilters.filter((value) => isMajorFilter(value));
    const currentSeoFilterIsMajor = isMajorFilter(seoFilterValue);
    const hasAnotherMajorFilter = currentSeoFilterIsMajor && activeMajorFilters.some((value) => value !== seoFilterValue);
    if (hasFilter(filterChoose, seoFilterValue) &&!hasAnotherMajorFilter) return;

    if (skipMissingUrlFilterNameRef.current === seoFilterValue) {
      return;
    }
    preventPromotionForSignatureRef.current = filterSignature;

    if (mode === "city_filter" && currentUrlPage?.city_slug) {
      navigate({
        pathname: `/${currentUrlPage.city_slug}`,
        search: searchQuery,
      }, { replace: true });
      return;
    }

    if (mode === "filter") {
      const remainingFilters = filterChoose.length;
      if (remainingFilters > 0) {
        navigate({
          pathname: "/",
          search: searchQuery,
        }, {replace: true});
        return;
      }
      navigate({
        pathname: "/restaurants",
        search: searchQuery,
      }, { replace: true });
    }
  }, [filterChoose,filterSignature,currentUrlPage,mode,indexablePagesLoaded,navigate,searchQuery]);

  
  const seoCandidate = useMemo(() => {
      const stringFilters = filterChoose.filter((filter): filter is string => typeof filter === "string" );
      const majorFilters = stringFilters.filter((value) => isMajorFilter(value));
      if (majorFilters.length > 1) {
        return null;
      }
      if (majorFilters.length === 1) {
        return majorFilters[0];
      }
      if (filterChoose.length === 1 && stringFilters.length === 1) {
        return stringFilters[0];
      }
      return null;
  }, [filterChoose,indexablePages]);

  const canPromoteToSeoPage = () => {
    if (!seoCandidate) return false;
    if (preventPromotionForSignatureRef.current ===filterSignature) {
      return false;
    }

    if (preventPromotionForSignatureRef.current && preventPromotionForSignatureRef.current !== filterSignature) {
      preventPromotionForSignatureRef.current = null;
    }
    return true;
  };


  useEffect(() => {
    if (isCleaningAfterPopRef.current) return;
    if (!indexablePagesLoaded) return;

    if (mode !== "city") return;
    if (!routeCity) return;
    if (!canPromoteToSeoPage()) return;
   const match = indexablePages.find((page) => {
        if (page.page_kind !=="city_filter") {
          return false;
        }
        if (page.city_slug !==routeCity) {
          return false;
        }
        return getFilterValues(page).includes(
          seoCandidate!
        );
    });

    if (!match?.slug_path) return;

    const nextPath = normalizePath(match.slug_path);
    if (nextPath === currentPath) return;

    const nextSearch = removeFilterFromSearch(searchQuery,seoCandidate!);
    navigate({pathname: nextPath, search: nextSearch}, { replace: true });
  }, [filterChoose,filterSignature,seoCandidate,indexablePages,indexablePagesLoaded,mode,routeCity,currentPath,navigate,searchQuery]);

  useEffect(() => {
    if (isCleaningAfterPopRef.current) return;
    if (!indexablePagesLoaded) return;
    if (mode !== "home_filter") return;
    if (!canPromoteToSeoPage()) return;

    const match = indexablePages.find((page) => {
        if (page.page_kind !=="filter") {
          return false;
        }
        return getFilterValues(page).includes(seoCandidate!);
    });


    if (!match?.slug_path) return;

    const nextPath = normalizePath(match.slug_path);
    if (nextPath === currentPath) return;

    const nextSearch = removeFilterFromSearch(searchQuery,seoCandidate!);
    navigate({pathname: nextPath, search: nextSearch}, { replace: true });
  }, [filterChoose,filterSignature,seoCandidate,indexablePages,indexablePagesLoaded,mode,currentPath,navigate,searchQuery]);
}